import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import api, { SESSION_EXPIRED_EVENT, TOKEN_KEY } from "@/lib/api";

const AuthContext = createContext(null);

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [authError, setAuthError] = useState(null);

    const checkAuth = useCallback(async () => {
        const token = localStorage.getItem(TOKEN_KEY);
        if (!token) {
            setUser(null);
            setLoading(false);
            return;
        }
        try {
            const res = await api.get("/api/auth/me");
            setUser(res.data.user);
            setAuthError(null);
        } catch (err) {
            setAuthError(err.response?.data?.detail || "Unable to refresh your session");
            localStorage.removeItem(TOKEN_KEY);
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    // The api client raises this once a refresh has failed, so an expired
    // session clears the UI instead of leaving a signed-in-looking navbar
    // above a page where every request fails.
    useEffect(() => {
        const onExpired = () => {
            setUser(null);
            setAuthError("Your session expired. Please sign in again.");
        };
        window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
        return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
    }, []);

    const login = useCallback(async (email, password) => {
        const res = await api.post("/api/auth/login", { email, password });
        if (res.data.access_token) {
            localStorage.setItem(TOKEN_KEY, res.data.access_token);
        }
        setUser(res.data.user);
        setAuthError(null);
        return res.data;
    }, []);

    const register = useCallback(async (email, password, name) => {
        const res = await api.post("/api/auth/register", { email, password, name });
        if (res.data.access_token) {
            localStorage.setItem(TOKEN_KEY, res.data.access_token);
        }
        setUser(res.data.user);
        setAuthError(null);
        return res.data;
    }, []);

    const logout = useCallback(async () => {
        try {
            await api.post("/api/auth/logout");
        } catch {
            // ignore
        }
        localStorage.removeItem(TOKEN_KEY);
        // Bug 6: clear saved wizard progress so re-login always starts fresh
        try { sessionStorage.removeItem("wizard_progress"); } catch (_) {}
        setUser(null);
        setAuthError(null);
    }, []);

    const loginWithToken = useCallback(async (token) => {
        localStorage.setItem(TOKEN_KEY, token);
        try {
            const res = await api.get("/api/auth/me", {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUser(res.data.user);
            setAuthError(null);
            return true;
        } catch (err) {
            localStorage.removeItem(TOKEN_KEY);
            setUser(null);
            setAuthError(err.response?.data?.detail || "Unable to sign in with this token");
            return false;
        }
    }, []);

    const value = useMemo(
        () => ({ user, loading, authError, login, register, logout, loginWithToken, checkAuth }),
        [user, loading, authError, login, register, logout, loginWithToken, checkAuth]
    );

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}
